import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import StatusBadge from "@/components/StatusBadge";
import { SERVICE_BY_KEY } from "@/lib/services";
import { formatDate } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, X, Kanban } from "@phosphor-icons/react";
import { toast } from "sonner";

const FILTERS = ["all", "pending", "approved", "in_progress", "completed", "rejected"];

export default function AdminDashboard() {
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [note, setNote] = useState("");
  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: projs }, { data: st }] = await Promise.all([
        api.get(`/admin/projects`, { params: { status_filter: filter } }),
        api.get(`/admin/stats`),
      ]);
      setProjects(projs);
      setStats(st);
    } catch (e) {
      toast.error("Failed to load admin data");
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openAction = (project, action) => {
    setModal({ project, action });
    setNote(project.admin_note || "");
    setInvoiceAmount("");
  };

  const closeModal = () => {
    setModal(null);
    setNote("");
    setInvoiceAmount("");
  };

  const performAction = async (statusOverride) => {
    if (!modal) return;
    const status = statusOverride || (modal.action === "approve" ? "approved" : "rejected");
    setSaving(true);
    try {
      const payload = {
        status,
        admin_note: note || null,
      };

      if (status === "approved" && invoiceAmount !== "") {
        payload.invoice_amount = Number(invoiceAmount);
      }

      await api.patch(`/admin/projects/${modal.project.id}`, payload);
      toast.success(`Project ${status.replace("_"," ")}`);
      closeModal();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <AppShell subtitle="◇ ADMIN · COMMAND CENTER" title="All incoming briefs">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 border-t border-l border-black/10 mb-8">
          <div className="border-r border-b border-black/10 p-6 bg-[#0B1F3A] text-white" data-testid="admin-stat-total">
            <div className="fx-mono-tag" style={{color:'#a3a3a3'}}>TOTAL</div>
            <div className="mt-2 text-4xl font-black tracking-tighter font-mono">{stats.total_projects}</div>
          </div>
          {["pending","approved","in_progress","completed","rejected"].map(k => (
            <div key={k} className="border-r border-b border-black/10 p-6" data-testid={`admin-stat-${k}`}>
              <div className="fx-mono-tag">{k.replace("_"," ").toUpperCase()}</div>
              <div className="mt-2 text-4xl font-black tracking-tighter font-mono">{stats.by_status[k] || 0}</div>
            </div>
          ))}
        </div>
      )}

      <Tabs value={filter} onValueChange={setFilter} className="mb-6">
        <TabsList className="bg-white border border-black/10 rounded-md h-auto p-1 flex-wrap">
          {FILTERS.map(f => (
            <TabsTrigger key={f} value={f} className="fx-mono-tag data-[state=active]:bg-black data-[state=active]:text-white rounded-md px-4 py-2" data-testid={`filter-${f}`}>
              {f.replace("_"," ").toUpperCase()}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="border border-black/10 overflow-hidden">
        <div className="grid grid-cols-12 border-b border-black/10 bg-[#F4F6F8] px-4 py-3 fx-mono-tag">
          <div className="col-span-4">PROJECT</div>
          <div className="col-span-3">CLIENT</div>
          <div className="col-span-2">SERVICE</div>
          <div className="col-span-1">STATUS</div>
          <div className="col-span-2 text-right">ACTIONS</div>
        </div>
        {loading ? (
          <div className="p-16 text-center text-neutral-500">Loading…</div>
        ) : projects.length === 0 ? (
          <div className="p-16 text-center text-neutral-500 flex flex-col items-center gap-3">
            <Kanban size={32} weight="duotone" />
            <div>No projects for this filter.</div>
          </div>
        ) : projects.map(p => {
          const svc = SERVICE_BY_KEY[p.service_type];
          return (
            <div key={p.id} className="grid grid-cols-12 border-b border-black/5 px-4 py-4 items-center hover:bg-[#F8FAFC] transition-colors" data-testid={`admin-row-${p.id}`}>
              <div className="col-span-4">
                <div className="font-semibold text-sm tracking-tight line-clamp-1">{p.title}</div>
                <div className="text-xs text-neutral-500 mt-1 line-clamp-1 font-mono">{formatDate(p.created_at)} · {p.budget || "no budget"} · {p.timeline || "no timeline"}</div>
              </div>
              <div className="col-span-3">
                <div className="text-sm">{p.user_name}</div>
                <div className="text-xs text-neutral-500 font-mono">{p.user_email}</div>
              </div>
              <div className="col-span-2 text-sm">{svc?.name}</div>
              <div className="col-span-1"><StatusBadge status={p.status} /></div>
              <div className="col-span-2 flex justify-end gap-2">
                {p.status === "pending" && (
                  <>
                    <Button size="sm" onClick={() => openAction(p, "approve")} className="fx-btn-accent rounded-md h-9" data-testid={`approve-btn-${p.id}`}>
                      <Check size={14} weight="bold" className="mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openAction(p, "reject")} className="rounded-md h-9 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white" data-testid={`reject-btn-${p.id}`}>
                      <X size={14} weight="bold" className="mr-1" /> Reject
                    </Button>
                  </>
                )}
                {["approved","in_progress"].includes(p.status) && (
                  <Button size="sm" variant="outline" onClick={() => openAction(p, "advance")} className="rounded-md h-9 border-black/20" data-testid={`advance-btn-${p.id}`}>
                    Advance →
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!modal} onOpenChange={(o) => !o && closeModal()}>
        <DialogContent className="rounded-md" data-testid="admin-action-modal">
          <DialogHeader>
            <DialogTitle className="tracking-tight">
              {modal?.action === "approve" && "Approve project"}
              {modal?.action === "reject" && "Reject project"}
              {modal?.action === "advance" && "Advance status"}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-neutral-600">
            <div className="fx-mono-tag mb-2">{modal?.project?.title}</div>
            <p className="mb-3">Add an optional note visible to the client:</p>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add context (optional)…" className="min-h-28" data-testid="admin-note-input" />

            {modal?.action === "approve" && (
              <div className="mt-4">
                <label htmlFor="invoice-amount" className="fx-mono-tag block mb-2">
                  Invoice amount in AED (optional)
                </label>
                <Input
                  id="invoice-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  inputMode="decimal"
                  value={invoiceAmount}
                  onChange={(e) => setInvoiceAmount(e.target.value)}
                  placeholder="Leave blank to approve without an invoice"
                  data-testid="admin-invoice-amount-input"
                />
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            {modal?.action === "approve" && (
              <Button disabled={saving} onClick={() => performAction("approved")} className="fx-btn-accent rounded-md" data-testid="confirm-approve-btn">Confirm approve</Button>
            )}
            {modal?.action === "reject" && (
              <Button disabled={saving} onClick={() => performAction("rejected")} className="rounded-md bg-[#FF3B30] hover:bg-[#c72e26] text-white" data-testid="confirm-reject-btn">Confirm reject</Button>
            )}
            {modal?.action === "advance" && (
              <>
                <Button disabled={saving} onClick={() => performAction("in_progress")} className="fx-btn-primary rounded-md" data-testid="confirm-inprogress-btn">Mark In Progress</Button>
                <Button disabled={saving} onClick={() => performAction("completed")} className="fx-btn-accent rounded-md" data-testid="confirm-completed-btn">Mark Completed</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
